import axios from 'axios';
import { setupAxiosInterceptors } from '../utils/axiosSetup';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
  timeout: 30000,
  withCredentials: true,
});

setupAxiosInterceptors(apiClient);

export interface ValidateReferenceDocument {
  actionDocumentId: number;
  documentTitle?: string | null;
  documentDescription?: string | null;
  spItemId?: number | null;
  fileName?: string | null;
  webUrl?: string | null;
}

export interface ValidateActionDetails {
  actionId: number;
  actionType: string;
  name?: string;
  instructions?: string;
  projectName?: string;
  phaseName?: string;
  phaseDescription?: string;
  taskName?: string;
  taskDescription?: string;
  taskId?: number;
  projectId?: number;
  status?: string;
  referenceDocuments?: ValidateReferenceDocument[];
}

export interface CompleteValidateResponse {
  success: boolean;
  message: string;
  actionId: number;
  actionType: string;
  status: string;
  actualEndDate: string;
  kpiScore: number;
  comment?: string | null;
}

export interface ValidateApiError {
  error: boolean;
  message: string;
  code?: string;
}

export async function getValidateActionDetails(
  token: string | null,
  actionId?: number | null
): Promise<ValidateActionDetails> {
  try {
    const url = token
      ? `/api/validate/details?token=${encodeURIComponent(token)}`
      : actionId
        ? `/api/validate/details?actionId=${actionId}`
        : '/api/validate/details';
    const response = await apiClient.get(url);
    return response.data;
  } catch (error: unknown) {
    console.warn('Could not fetch validate action details:', error);
    return {
      actionId: actionId || 0,
      actionType: 'VALIDATE',
      referenceDocuments: [],
    };
  }
}

export async function completeValidate(
  token: string | null,
  data: { comment?: string },
  actionId?: number | null
): Promise<CompleteValidateResponse> {
  const url = token
    ? `/api/validate/complete?token=${encodeURIComponent(token)}`
    : actionId
      ? `/api/validate/complete?actionId=${actionId}`
      : '/api/validate/complete';
  const requestData = actionId && !token ? { ...data, actionId } : data;
  try {
    const response = await apiClient.post(url, requestData, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
      timeout: 60000,
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { status?: number; data?: { message?: string; code?: string } }; message?: string };
    const status = err.response?.status;
    const errorData = err.response?.data;
    throw {
      error: true,
      message: errorData?.message || err.message || 'Failed to complete validate action.',
      code: errorData?.code || (status === 401 ? 'UNAUTHORIZED' : status === 403 ? 'NOT_ASSIGNED' : 'NETWORK_ERROR'),
    } as ValidateApiError;
  }
}
